import React, { forwardRef } from 'react';

const ExtrasPrint = forwardRef((props, ref) => {
    const {
        venteData = {},
        company = {}
    } = props;

    const items = Array.isArray(venteData.items) ? venteData.items : [];
    const clientNom = venteData.client_nom || "Client Divers";
    const numeroRecu = venteData.reference_recu || 'REC-' + Date.now().toString().slice(-6);
    const totalTTC = Number(venteData.montant_total || 0);
    const montantRecu = Number(venteData.montant_recu || totalTTC);
    const monnaieRendue = Math.max(0, montantRecu - totalTTC);

    const getLogoSrc = () => {
        const data = company.logo_data || company.logo || company.logo_url;
        if (!data) return null;
        if (data.startsWith('data:') || data.startsWith('http') || data.startsWith('blob:')) {
            return data;
        }
        return `data:image/png;base64,${data}`;
    };

    const logoSrc = getLogoSrc();

    return (
        <div 
            ref={ref} 
            className="facture-extra-print"
            style={{
                fontFamily: '"Courier New", Courier, monospace', // Police style ticket de caisse plus pro
                color: '#0f172a',
                background: '#ffffff',
                padding: '10px',
                boxSizing: 'border-box',
                width: '100%',
                maxWidth: '80mm',
                margin: '0 auto',
                fontSize: '12px',
                lineHeight: '1.3',
                display: 'flex',
                flexDirection: 'column'
            }}
        >
            <style>
                {`
                    @media print {
                        @page {
                            size: 80mm auto;
                            margin: 0mm;
                        }
                        body {
                            margin: 0 !important;
                            padding: 0 !important;
                            background-color: #ffffff !important;
                            -webkit-print-color-adjust: exact;
                        }
                        .facture-extra-print {
                            width: 80mm !important;
                            max-width: 80mm !important;
                            margin: 0 auto !important;
                            padding: 5mm !important;
                            box-shadow: none !important;
                        }
                    }
                `}
            </style>

            {/* En-tête de l'établissement */}
            <div style={{ textAlign: 'center', borderBottom: '2px dashed #0f172a', paddingBottom: '8px', marginBottom: '8px' }}>
                {logoSrc && (
                    <img src={logoSrc} alt="Logo" style={{ maxHeight: '14mm', maxWidth: '35mm', objectFit: 'contain', marginBottom: '4px', display: 'block', margin: '0 auto 4px auto' }} />
                )}
                <h2 style={{ margin: '0 0 2px 0', fontSize: '15px', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {company.name || company.nom || "HÔTEL CHAÎNE B"}
                </h2>
                <p style={{ margin: '2px 0', color: '#334155', fontSize: '10px' }}>{company.address || company.adresse || "Adresse de l'établissement"}</p>
                <p style={{ margin: '2px 0', color: '#334155', fontSize: '10px' }}>Tél : {company.phone || company.telephone || "N/A"}</p>
            </div>

            {/* Infos Reçu / Client */}
            <div style={{ marginBottom: '10px', fontSize: '10.5px', borderBottom: '1px solid #cbd5e1', paddingBottom: '6px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '3px' }}>
                    <span><strong>Ticket :</strong> {numeroRecu}</span>
                    <span>{new Date().toLocaleDateString('fr-FR')} {new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</span>
                </div>
                <div style={{ marginBottom: '3px' }}>
                    <strong>Client :</strong> {clientNom}
                </div>
                <div>
                    <strong>Règlement :</strong> {venteData.mode_reglement || 'ESPECES'}
                </div>
            </div>

            {/* Tableau des articles */}
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '10px', fontSize: '10.5px' }}>
                <thead>
                    <tr style={{ borderBottom: '2px solid #0f172a', borderTop: '1px solid #0f172a' }}>
                        <th style={{ padding: '5px 2px', textAlign: 'left', width: '45%' }}>Désign.</th>
                        <th style={{ padding: '5px 2px', textAlign: 'center', width: '15%' }}>Qté</th>
                        <th style={{ padding: '5px 2px', textAlign: 'right', width: '20%' }}>P.U</th>
                        <th style={{ padding: '5px 2px', textAlign: 'right', width: '20%' }}>Total</th>
                    </tr>
                </thead>
                <tbody>
                    {items.map((item, idx) => (
                        <tr key={idx} style={{ borderBottom: '1px dotted #cbd5e1' }}>
                            <td style={{ padding: '5px 2px', textAlign: 'left', wordBreak: 'break-word' }}>{item.nom}</td>
                            <td style={{ padding: '5px 2px', textAlign: 'center' }}>{item.quantite}</td>
                            <td style={{ padding: '5px 2px', textAlign: 'right' }}>{Number(item.prix_unitaire).toLocaleString()}</td>
                            <td style={{ padding: '5px 2px', textAlign: 'right', fontWeight: 'bold' }}>{Number(item.montant_ttc).toLocaleString()}</td>
                        </tr>
                    ))}
                </tbody>
            </table>

            {/* Totaux */}
            <div style={{ borderTop: '2px dashed #0f172a', paddingTop: '8px', marginBottom: '10px', fontSize: '11.5px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: '800', fontSize: '13px', marginBottom: '6px' }}>
                    <span>NET À PAYER :</span>
                    <span>{totalTTC.toLocaleString()} F</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', color: '#334155', fontSize: '10.5px', marginBottom: '2px' }}>
                    <span>Montant versé :</span>
                    <span>{montantRecu.toLocaleString()} F</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', color: '#047857', fontSize: '11px', fontWeight: 'bold' }}>
                    <span>Monnaie rendue :</span>
                    <span>{monnaieRendue.toLocaleString()} F</span>
                </div>
            </div>

            {/* Pied de page */}
            <div style={{ textAlign: 'center', marginTop: 'auto', paddingTop: '8px', borderTop: '1px solid #cbd5e1', fontSize: '9.5px', color: '#475569' }}>
                <p style={{ margin: '0 0 3px 0', fontWeight: 'bold' }}>✨ Merci pour votre visite !</p>
                <p style={{ margin: 0 }}>À bientôt dans notre établissement.</p>
            </div>
        </div>
    );
});

ExtrasPrint.displayName = 'ExtrasPrint';
export default ExtrasPrint;